import { RoutalPalette } from '@nx-smartmonkey/ui';
import { simplify } from '@turf/turf';
import _ from 'lodash';
import { ActionMeta, OnChangeValue } from 'react-select';
import AsyncSelect from 'react-select/async';
import { intl } from '../helpers/IntGlobalProvider';
import { useShowSnackbarMessage } from '../hooks/snackbarMessage/useShowSnackbarMessage';
import Text from '../react-components/Text';

interface Option {
  label: string;
  place_id: string;
}

const mapOptionsToValues = (results: any) => {
  return results.map((r: any) => ({
    label: r.display_name,
    place_id: r.place_id,
  }));
};

type LocationSearchProps = {
  onUpdate: ({
    placeLabel,
    placeCoordinates,
  }: {
    placeLabel?: string;
    placeCoordinates?: [number, number][][][];
  }) => void;
  placeholder?: string;
  loadingMessage?: string;
  noOptionsMessage?: string;
  menuPlacement?: `bottom` | `top`;
};

export const PlaceSearch = ({
  onUpdate,
  placeholder,
  loadingMessage,
  noOptionsMessage,
  menuPlacement,
}: LocationSearchProps) => {
  const { showSnackbarMessage } = useShowSnackbarMessage();
  return (
    <AsyncSelect
      cacheOptions
      autoFocus
      // defaultOptions
      menuPlacement={menuPlacement}
      styles={{
        control: (baseStyles, state) => {
          const { outline, ...provided } = baseStyles;
          return {
            ...provided,
            borderColor: state.isFocused ? RoutalPalette.primary00 : RoutalPalette.neutral20,
            minHeight: `unset`,
            fontSize: `12px`,
            cursor: `pointer`,
            boxShadow: `none`,
            backgroundColor: `transparent`,
            '&:hover': {
              backgroundColor: RoutalPalette.neutral05,
            },
          };
        },
        dropdownIndicator: (baseStyles, state) => ({
          ...baseStyles,
          display: `none`,
        }),
        option: (baseStyles, state) => {
          return {
            ...baseStyles,
            fontSize: `12px`,
            color: RoutalPalette.text,
            cursor: `pointer`,
            fontFamily: `Geist Variable`,
            backgroundColor: `transparent`,
          };
        },
        menu: (baseStyles, state) => {
          return {
            ...baseStyles,
            borderRadius: 4,
            boxShadow: `none`,
            border: `1px solid ${RoutalPalette.neutral20}`,
          };
        },
      }}
      placeholder={<Text variant="label">{placeholder ?? ``}</Text>}
      loadingMessage={(obj: { inputValue: string }) => (
        <Text variant="label">{loadingMessage ?? intl.formatMessage({ id: `loading` })}</Text>
      )}
      noOptionsMessage={(obj: { inputValue: string }) => (
        <Text variant="label">{noOptionsMessage ?? intl.formatMessage({ id: `none` })}</Text>
      )}
      onChange={async (newValue: OnChangeValue<Option, false>, actionMeta: ActionMeta<Option>) => {
        if (actionMeta.action === `select-option`) {
          if (!newValue?.place_id) {
            onUpdate({});
            return;
          }

          await fetch(
            `https://nominatim.openstreetmap.org/details?place_id=${newValue.place_id}&format=json&polygon_geojson=1`
          )
            .then(async (response) => {
              const result = await response.json();
              if (result.geometry.type === `Point`) {
                showSnackbarMessage({ messageIntlId: `infoerror.geo_fence_should_be_an_area` });
              } else {
                simplify(result.geometry, {
                  mutate: true,
                  tolerance: 0.001,
                  // highQuality: true,
                });

                const placeCoordinates =
                  result.geometry.type === `MultiPolygon` ? result.geometry.coordinates : [result.geometry.coordinates];

                onUpdate({ placeLabel: newValue.label, placeCoordinates });
              }
            })
            .catch((error) => {
              onUpdate({});
            });
        }
      }}
      loadOptions={_.debounce((inputValue: string, callback: any) => {
        fetch(`https://nominatim.openstreetmap.org/search?q=${inputValue}&format=json`)
          .then(async (response) => {
            const results = await response.json();
            const relations = results.filter((result: any) => result.osm_type === `relation`);
            callback(mapOptionsToValues(relations));
          })
          .catch((error) => {
            callback([]);
          });
      }, 300)}
    />
  );
};
